import {zodResolver} from '@hookform/resolvers/zod';
import {useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';

import {Button} from '@/components/ui/button';
import type {DialogProps} from '@/components/ui/dialog';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogSection,
  DialogTitle,
} from '@/components/ui/dialog';
import {Form} from '@/components/ui/form';
import {useToast} from '@/components/ui/use-toast';
import {useMyUser} from '@/hooks/useUsers';
import {graphqlClient} from '@/services/graphqlClient';
import {
  useCreateRecurringTaskMutation,
  useCreateTaskMutation,
} from '@/services/graphqlHooks';

import {markAnnouncementsAsViewed} from '../../utils/announcementTasks';
import type {TaskFormValues} from '../../utils/tasksFormInputs';
import {
  mapTaskFormValuesToRecurringTaskInput,
  mapTaskFormValuesToTaskInput,
  StandardTaskVariant,
  taskFormDefaultValues,
  taskFormSchema,
} from '../../utils/tasksFormInputs';
import TaskFormFields from '../TaskFormFields';

export type EditTaskDialogProps = DialogProps;

const AddTaskDialog = ({onOpenChange, ...props}: EditTaskDialogProps) => {
  const {t} = useTranslation();

  const currentUserData = useMyUser();
  const {toast} = useToast();

  const onSuccess = (taskId: string) => {
    if (!taskId || !currentUserData?.fullName) return;

    markAnnouncementsAsViewed([taskId], currentUserData?.fullName);
  };

  const createTask = useCreateTaskMutation(graphqlClient, {
    onSuccess: data => onSuccess(data?.createTask.id),
  });
  const createRecurringTask = useCreateRecurringTaskMutation(graphqlClient, {
    onSuccess: data => onSuccess(data?.createRecurringTask.id),
  });
  const isDisabled = createTask.isPending || createRecurringTask.isPending;
  const form = useForm<TaskFormValues>({
    resolver: zodResolver(taskFormSchema),
    defaultValues: taskFormDefaultValues(),
  });

  const handleSubmit = async (values: TaskFormValues) => {
    const isRecurring =
      values.standardTaskVariant === StandardTaskVariant.Recurring;
    try {
      isRecurring
        ? await createRecurringTask.mutateAsync({
            input: mapTaskFormValuesToRecurringTaskInput(values),
          })
        : await createTask.mutateAsync({
            input: mapTaskFormValuesToTaskInput(values),
          });

      toast({
        title: t('Task created'),
        variant: 'success',
      });
      onOpenChange?.(true);
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <Form {...form}>
      <Dialog {...props} onOpenChange={onOpenChange}>
        <DialogContent asChild>
          <form onSubmit={form.handleSubmit(handleSubmit)} noValidate>
            <DialogHeader className="md:hidden">
              <DialogTitle>{t('Task')}</DialogTitle>
            </DialogHeader>
            <DialogSection className="py-0">
              <TaskFormFields form={form} />
            </DialogSection>
            <DialogFooter>
              <Button
                type="button"
                variant="secondary"
                onClick={() => onOpenChange?.(false)}
              >
                {t('Close')}
              </Button>
              <Button variant="primary" type="submit" disabled={isDisabled}>
                {t('Submit')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Form>
  );
};

export default AddTaskDialog;
